
import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent]
 * @property {import('./slideshow').Slideshow} [slideshow]
 * @property {HTMLElement[]} [media]
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal }
    );

    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      { signal }
    );

    this.slideshow?.addEventListener(
      SlideshowSelectEvent.eventName,
      this.#handleSlideshowSelect,
      { signal }
    );

    requestAnimationFrame(() => this.#syncInitialVideo());
  }

  #controller = new AbortController();
  #playRequest = 0;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const newMediaGallery = detail.html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[media-gallery] Event promise rejected:', error);
        }
      });
  };

  #handleZoomMediaSelected = (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  #handleSlideshowSelect = (event) => {
    const detail = event.detail;
    const mediaItems = this.media;

    if (!mediaItems?.length) return;

    if (detail?.slide instanceof HTMLElement) {
      this.#activateVideo(detail.slide);
      return;
    }

    if (typeof detail?.index === 'number') {
      const activeItem = mediaItems[detail.index];
      if (activeItem) this.#activateVideo(activeItem);
      return;
    }

    this.#syncInitialVideo();
  };

  #syncInitialVideo() {
    const mediaItems = this.media;

    if (!mediaItems?.length) return;

    const activeItem =
      mediaItems.find((item) =>
        item.classList.contains('is-active') ||
        item.getAttribute('aria-hidden') === 'false'
      ) || mediaItems[0];

    this.#activateVideo(activeItem);
  }

  #activateVideo(activeItem) {
    const requestId = ++this.#playRequest;
    const mediaItems = this.media || [];

    mediaItems.forEach((item) => {
      item.querySelectorAll('video').forEach((video) => {
        if (!activeItem.contains(video)) {
          video.pause();
        }
      });
    });

    const deferredMedia = activeItem.matches?.('deferred-media')
      ? activeItem
      : activeItem.querySelector?.('deferred-media');

    if (!deferredMedia) return;

    if (!deferredMedia.hasAttribute('data-media-loaded')) {
      deferredMedia.loadContent(false);
    }

    const video = deferredMedia.querySelector('video');

    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.controls = false;
    video.playsInline = true;

    video.setAttribute('muted', '');
    video.setAttribute('loop', '');
    video.removeAttribute('controls');
    video.setAttribute('playsinline', '');

    const startPlayback = () => {
      if (requestId !== this.#playRequest) return;
      if (!video.isConnected || !activeItem.isConnected) return;

      const playPromise = video.play();

      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          if (error?.name !== 'AbortError') {
            console.warn('[media-gallery] Video playback failed:', error);
          }
        });
      }
    };

    if (video.readyState >= 2) {
      startPlayback();
    } else {
      video.addEventListener('canplay', startPlayback, {
        once: true,
        signal: this.#controller.signal
      });
    }
  }

  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];

    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
